import { create } from 'zustand';

/**
 * Largeur de l'emplacement latéral droit (panneau d'aperçu), redimensionnable et
 * persistée en localStorage — calque de `useSidebarLayout` pour le bord opposé.
 *
 * WHY un store maison plutôt qu'un `PanelGroup` de `react-resizable-panels` :
 * cette largeur doit être lisible par des surfaces qui vivent HORS de l'arbre du
 * panneau — le wizard bordereau et la dialog de renumérotation sont des portails
 * Radix montés sur `document.body`, et se décalent pour rester côte à côte avec
 * l'aperçu. Un split rend des pourcentages en style inline, inatteignables depuis
 * un portail ; il faudrait de toute façon publier une variable CSS. À quoi
 * s'ajoutent deux raisons de fond : un dimensionnement en pourcentage ferait varier
 * l'aperçu quand on tire la barre de gauche, et le passage sous `md` (overlay plein
 * écran) imposerait un arbre différent par breakpoint, donc un démontage — tous les
 * PDF ouverts se rechargeraient à la moindre rotation de tablette.
 */
export const PREVIEW_PANEL_MIN_WIDTH = 320;
/** Borne haute : au-delà, le contenu principal n'a plus de quoi se lire. */
export const PREVIEW_PANEL_MAX_WIDTH = 960;
/** Part de l'écran prise par défaut — reproduit le `45vw` d'avant le redimensionnement. */
export const PREVIEW_PANEL_DEFAULT_RATIO = 0.45;

const WIDTH_KEY = 'hector-preview-width';

function clampWidth(width: number): number {
  // La borne haute suit la fenêtre : `min(60vw, 960px)`, pour qu'un panneau
  // enregistré sur un grand écran n'avale pas un petit écran à la réouverture.
  const viewportMax = typeof window === 'undefined'
    ? PREVIEW_PANEL_MAX_WIDTH
    : Math.max(PREVIEW_PANEL_MIN_WIDTH, Math.round(window.innerWidth * 0.6));
  const max = Math.min(PREVIEW_PANEL_MAX_WIDTH, viewportMax);
  return Math.min(max, Math.max(PREVIEW_PANEL_MIN_WIDTH, Math.round(width)));
}

function defaultWidth(): number {
  if (typeof window === 'undefined') return PREVIEW_PANEL_MIN_WIDTH;
  return clampWidth(window.innerWidth * PREVIEW_PANEL_DEFAULT_RATIO);
}

function readInitialWidth(): number {
  try {
    const stored = localStorage.getItem(WIDTH_KEY);
    if (!stored) return defaultWidth();
    const parsed = Number.parseInt(stored, 10);
    // Une valeur illisible (stockage corrompu, ancienne unité) retombe sur le
    // défaut plutôt que de produire un `NaN` qui figerait le panneau à 0.
    return Number.isFinite(parsed) ? clampWidth(parsed) : defaultWidth();
  } catch {
    return defaultWidth();
  }
}

interface PreviewPanelLayoutStore {
  width: number;
  setWidth: (width: number) => void;
}

export const usePreviewPanelLayout = create<PreviewPanelLayoutStore>()((set) => ({
  width: readInitialWidth(),
  setWidth: (width) => {
    const clamped = clampWidth(width);
    try {
      localStorage.setItem(WIDTH_KEY, String(clamped));
    } catch {
      // Navigation privée / stockage bloqué : la largeur reste vivante en mémoire.
    }
    set({ width: clamped });
  },
}));
